import { useEffect, useState, type FormEvent } from "react";
import { ImagePlus, PenLine, RefreshCw, Save, Trash2, Wand2 } from "lucide-react";
import { ColorPicker, MailSignaturePreview } from "@wa/ui";
import { useT } from "../../../../i18n/I18nProvider";
import { MediaPicker } from "../../../../components/MediaPicker";
import { ApiError } from "../../../../lib/api";
import {
  getSystemSignature, saveSystemSignature,
  type SignatureDraft, type SystemSignatureView,
} from "../../../../lib/mailSignature";
import { Btn, IconBtn, StatusPill } from "../../../ui";
import { ConnStatus, IntegrationCard, PanelLoader } from "../panel/IntegrationCard";
import { Field, FieldBox, Pill, SaveRow } from "../panel/fields";

const draftOf = (s: SystemSignatureView["signature"]): SignatureDraft => ({
  closing: s.closing, name: s.name, details: s.details, accent: s.accent, logoSource: s.logoSource,
});

/** The offer fills only what is still empty: it must never overwrite a phone
 *  number somebody has just typed. */
function withOffer(draft: SignatureDraft, offer: SystemSignatureView["offer"]): SignatureDraft {
  return {
    ...draft,
    name: draft.name.trim() || offer.name,
    details: draft.details.trim() || offer.details,
    accent: draft.accent || offer.accent,
    logoSource: draft.logoSource || offer.logoSource,
  };
}

const same = (a: SignatureDraft, b: SignatureDraft) =>
  a.closing === b.closing && a.name === b.name && a.details === b.details && a.accent === b.accent && a.logoSource === b.logoSource;

const isSet = (s: SignatureDraft) => Boolean(s.closing || s.name || s.details || s.logoSource);

/**
 * The signature under every letter the CRM sends by itself.
 *
 * A card of its own, above the channels rather than inside the system one: the
 * signature follows the KIND of letter, not the account that carries it. The
 * CRM's notices to its own desk ride the platform's account and are signed here
 * all the same, so a field inside the system channel's card would say something
 * untrue about them.
 *
 * Not the mailbox's saved signatures either (`SignatureListField`). Those are
 * chosen per letter by whoever writes it; this one goes under letters nobody
 * wrote by hand.
 *
 * The same fields as the admin's signature form, drawn with this app's own
 * parts; the picture of the result is the kit's `MailSignaturePreview`, which
 * both share.
 */
export function SystemSignatureCard() {
  const t = useT();
  const [view, setView] = useState<SystemSignatureView | null>(null);
  const [draft, setDraft] = useState<SignatureDraft | null>(null);
  const [loading, setLoading] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error" | "logo">("idle");

  useEffect(() => {
    getSystemSignature()
      .then((v) => {
        setView(v);
        setDraft(draftOf(v.signature));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <PanelLoader />;
  if (!view || !draft) return null;

  const edit = (part: Partial<SignatureDraft>) => {
    setDraft({ ...draft, ...part });
    setSaveState("idle");
  };

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    if (!draft) return;
    setSaveState("saving");
    try {
      const next = await saveSystemSignature(draft);
      setView(next);
      // What the server kept, not what was typed: it trims and caps.
      setDraft(draftOf(next.signature));
      setSaveState("saved");
    } catch (err) {
      setSaveState(err instanceof ApiError && err.message === "logo_unusable" ? "logo" : "error");
    }
  }

  const offered = withOffer(draft, view.offer);
  const siteId = view.mediaSiteId;

  return (
    <IntegrationCard
      icon={PenLine}
      title={t("settings.channels.signature.title")}
      desc={t("settings.channels.signature.desc")}
      status={
        <ConnStatus
          ok={isSet(draftOf(view.signature))}
          okLabel={t("settings.channels.signature.on")}
          offLabel={t("settings.channels.signature.off")}
        />
      }
    >
      <form onSubmit={handleSave} className="space-y-4">
        <div className="grid gap-x-6 gap-y-4 lg:grid-cols-2">
          <div className="min-w-0 space-y-4">
            <Field label={t("settings.channels.signature.closing")}>
              <Pill
                value={draft.closing}
                onChange={(closing) => edit({ closing: closing.slice(0, view.limits.closing) })}
                placeholder={t("settings.channels.signature.closingPlaceholder")}
                autoComplete="off"
              />
            </Field>
            <Field label={t("settings.channels.signature.name")}>
              <Pill
                value={draft.name}
                onChange={(name) => edit({ name: name.slice(0, view.limits.name) })}
                placeholder="WebAdmin OÜ"
                autoComplete="off"
              />
            </Field>
            <Field
              label={t("settings.channels.signature.details")}
              hint={t("settings.channels.signature.detailsHint", { lines: view.limits.detailLines })}
            >
              {/* `crm-area`, not a textarea inside `crm-inp`: see `SignatureListField`. */}
              <textarea
                className="crm-area"
                rows={4}
                maxLength={view.limits.details}
                value={draft.details}
                placeholder={t("settings.channels.signature.detailsPlaceholder")}
                onChange={(e) => edit({ details: e.target.value })}
              />
            </Field>

            <div className="grid gap-4 sm:grid-cols-2">
              {/* `FieldBox`, not `Field`: these hold buttons, and a `<label>`
                  would forward its clicks to the first one inside it. */}
              <FieldBox label={t("settings.channels.signature.logo")} hint={t("settings.channels.signature.logoHint")}>
                {draft.logoSource ? (
                  <div className="flex items-center gap-2.5">
                    <span className="flex size-[52px] flex-none items-center justify-center rounded-2xl bg-[var(--crm-sunk)] p-2">
                      <img src={draft.logoSource} alt="" className="max-h-full max-w-full object-contain" />
                    </span>
                    <Btn small type="button" onClick={() => setPickerOpen(true)} disabled={!siteId}>
                      <ImagePlus className="size-3.5" />
                      {t("settings.channels.signature.logoChange")}
                    </Btn>
                    <IconBtn
                      small
                      type="button"
                      onClick={() => edit({ logoSource: "" })}
                      title={t("settings.channels.signature.logoRemove")}
                      aria-label={t("settings.channels.signature.logoRemove")}
                    >
                      <Trash2 className="size-3.5" />
                    </IconBtn>
                  </div>
                ) : (
                  <Btn small type="button" onClick={() => setPickerOpen(true)} disabled={!siteId}>
                    <ImagePlus className="size-3.5" />
                    {t("settings.channels.signature.logoPick")}
                  </Btn>
                )}
              </FieldBox>

              <FieldBox label={t("settings.channels.signature.accent")}>
                <div className="flex items-center gap-2.5">
                  <ColorPicker value={draft.accent || "#d4d4d4"} onChange={(accent) => edit({ accent })} />
                  {draft.accent ? (
                    <Btn small type="button" onClick={() => edit({ accent: "" })}>
                      {t("settings.channels.signature.accentClear")}
                    </Btn>
                  ) : null}
                </div>
              </FieldBox>
            </div>
          </div>

          <div className="min-w-0">
            <span className="crm-lab !mt-0">{t("settings.channels.signature.preview")}</span>
            <MailSignaturePreview
              className="rounded-3xl"
              body={t("settings.channels.signature.previewBody")}
              closing={draft.closing}
              name={draft.name}
              details={draft.details}
              accent={draft.accent}
              logoUrl={draft.logoSource}
              emptyLabel={t("settings.channels.signature.previewEmpty")}
            />
          </div>
        </div>

        <SaveRow>
          <Btn type="submit" tone="dark" small disabled={saveState === "saving"}>
            {saveState === "saving" ? <RefreshCw className="size-3.5 animate-spin" /> : <Save className="size-3.5" />}
            {saveState === "saving" ? t("settings.mail.saving") : t("settings.mail.save")}
          </Btn>
          {/* Offered only while it would change something. */}
          {!same(offered, draft) ? (
            <Btn small type="button" onClick={() => edit(offered)}>
              <Wand2 className="size-3.5" />
              {t("settings.channels.signature.fill")}
            </Btn>
          ) : null}
          {saveState === "saved" ? <StatusPill tone="ok">{t("settings.mail.saved")}</StatusPill> : null}
          {saveState === "error" ? <StatusPill tone="lt">{t("settings.mail.saveError")}</StatusPill> : null}
          {saveState === "logo" ? <StatusPill tone="lt">{t("settings.channels.signature.logoUnusable")}</StatusPill> : null}
        </SaveRow>
      </form>

      {siteId ? (
        <MediaPicker
          open={pickerOpen}
          siteId={siteId}
          onClose={() => setPickerOpen(false)}
          onPick={(asset) => {
            edit({ logoSource: asset.url });
            setPickerOpen(false);
          }}
        />
      ) : null}
    </IntegrationCard>
  );
}
